import styled from '@emotion/styled';
import { Button, message, Popconfirm, Table, Tag, Typography } from 'antd';
import { ColumnsType, TableProps } from 'antd/lib/table';
import { arrayMoveImmutable } from 'array-move';
import { isUndefined } from 'lodash-es';
import { TFnType, useTranslate } from '@tolgee/react';
import { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import {
  deletePhraseV2,
  PhraseAttachmentElement,
  PhrasesResourceElement,
  PhrasesRootData,
  PhrasesStickerElement,
  PhrasesTextElement,
  sortPhraseV2,
} from 'services/api/phrase';
import { useMemo } from 'react';
import { DragHandle, SortableItem, DraggableContainer } from 'components/DragTableComponent';
import useFormatShortKey from '../hooks/useFormatShortKey';

const { Text } = Typography;

const TextContent = styled.div`
  padding: 4px 0;

  &:not(:first-child) {
    border-top: 1px dashed #84868c;
  }
`;

const TagWrapper = styled.div`
  > * {
    margin-bottom: 4px;
  }
`;

export const phraseContentRender = (record: PhrasesRootData, t: TFnType) => {
  const { phraseMap } = record;
  const data = phraseMap[Object.keys(phraseMap)?.[0]];
  if (data) {
    const {
      phraseElems: { elems },
    } = data;
    const onlyText =
      elems.length > 0 && elems.every((el) => !isUndefined((el as PhrasesTextElement)?.text));
    if (onlyText) {
      const textObj = (elems?.[0] as PhrasesTextElement).text;
      return (
        <>
          <TextContent>
            <Text ellipsis={{ tooltip: textObj?.content }}>{textObj?.content}</Text>
          </TextContent>
          {textObj?.translationContent ? (
            <TextContent>
              <Text ellipsis={{ tooltip: textObj?.translationContent }}>
                {textObj.translationContent}
              </Text>
            </TextContent>
          ) : null}
        </>
      );
    }
    return elems.reduce((prev, cur) => {
      if ((cur as PhrasesTextElement)?.text) {
        return `${prev}[${t('phrase.文本')}]`;
      }
      if ((cur as PhrasesStickerElement)?.sticker) {
        return `${prev}[${t('messageType.STICKER')}]`;
      }
      if ((cur as PhrasesResourceElement)?.resource) {
        const resource = (cur as PhrasesResourceElement)?.resource;
        if (resource.resourceType === 1) {
          return `${prev}[${t('phrase.图片')}]`;
        }
        if (resource.resourceType === 2) {
          return `${prev}[${t('phrase.视频')}]`;
        }
      }
      if ((cur as PhraseAttachmentElement).attachment) {
        return `${prev}[${t('附件')}]`;
      }

      return '';
    }, '');
  }

  return '-';
};

export interface SortTableProps extends TableProps<PhrasesRootData> {
  platform: General.PlatformUpperCase;
  regionRange?: General.UpperCaseRegion[];
  canCrud?: boolean;
  onUpdate?: () => void;
  onEdit?: (data: PhrasesRootData) => void;
}

export const PhraseTable = (props: SortTableProps) => {
  const {
    platform,
    regionRange = [],
    dataSource: data = [],
    canCrud,
    onUpdate,
    onEdit,
    ...otherProps
  } = props;
  const { t } = useTranslate();

  const formatShortKey = useFormatShortKey();

  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      // 重新排序
      const sortNumber = data.map((d) => d.sortNumber);
      const sortedData = arrayMoveImmutable(data.slice(), oldIndex, newIndex);
      const sortMap: Record<string, string> = {};
      sortedData.forEach((d, i) => {
        sortMap[d.id] = sortNumber[i];
      });
      sortPhraseV2({
        sortMap,
      })
        .then(() => {
          onUpdate?.();
        })
        .catch(() => {
          message.error(t('api.common.error.messages', { action: t('api.common.action.request') }));
        });
    }
  };

  const Container = (props: SortableContainerProps) => (
    <DraggableContainer onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = data.findIndex((x) => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const columns: ColumnsType<PhrasesRootData> = useMemo(() => {
    const base: ColumnsType<PhrasesRootData> = [
      {
        title: t('phrase.快捷指令'),
        dataIndex: 'shortcutKey',
        className: 'drag-visible',
        width: 120,
        render(value) {
          return value ? formatShortKey(value) : '-';
        },
      },
      {
        title: t('phrase.标签'),
        dataIndex: 'tags',
        className: 'drag-visible',
        width: 180,
        render(_, record) {
          return (
            <TagWrapper>
              {record.tags.map((t) => (
                <Tag>{t}</Tag>
              ))}
            </TagWrapper>
          );
        },
      },
      {
        title: t('phrase.分组'),
        dataIndex: 'groupName',
        className: 'drag-visible',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('phrase.快捷回复'),
        dataIndex: 'address',
        className: 'drag-visible',
        render: (_, record) => phraseContentRender(record, t),
      },
    ];

    if (platform !== 'FACEBOOK') {
      base.push({
        title: t('phrase.站点'),
        dataIndex: 'phraseMap',
        className: 'drag-visible',
        render(_, record) {
          const regions = Object.keys(record.phraseMap);
          return (
            regionRange.length > 0
              ? regions.filter((region) => regionRange.includes(region))
              : regions
          ).join('|');
        },
      });
    }

    if (canCrud) {
      // 排序列
      base.unshift({
        title: '',
        dataIndex: 'sort',
        width: 30,
        className: 'drag-visible',
        render: () => <DragHandle />,
      });
      // 操作列
      base.push({
        title: t('操作'),
        className: 'drag-visible',
        width: 180,
        render(_, record) {
          return (
            <>
              <Button type="link" onClick={() => onEdit?.(record)}>
                {t('aide.编辑')}
              </Button>
              <Popconfirm
                title={t('phrase.确认删除该快捷回复吗')}
                placement="left"
                onConfirm={() => {
                  deletePhraseV2({
                    phraseRootId: record.id,
                  })
                    .then(() => {
                      onUpdate?.();
                      message.success(t('phrase.删除成功'));
                    })
                    .catch(() => {
                      message.success(t('phrase.删除失败，请稍后再试'));
                    });
                }}
              >
                <Button type="link">{t('phrase.删除')}</Button>
              </Popconfirm>
            </>
          );
        },
      });
    }
    return base;
  }, [platform, canCrud, regionRange, formatShortKey, onEdit, onUpdate, t]);

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="id"
      components={{
        body: {
          wrapper: Container,
          row: DraggableBodyRow,
        },
      }}
      {...otherProps}
    />
  );
};
